import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, CircularProgress, Grid2 as Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useParams } from 'react-router-dom';
import { useGetReport } from '@/admins/SmartReports/hooks/useGetReports';
import { STATIC_END_PAGES } from '../constants';
import { SmartReportLayouts } from '../Layouts';
import { EditPage } from './EditPage';
import { EditSmartReportProvider, useEditSmartReport } from './EditSmartReportContext';
import { EditSmartReportHeader } from './EditSmartReportHeader';
import { Pages } from './Pages';
import { SideBar } from './SideBar';

const useStyles = makeStyles(theme => ({
  center: {
    ...theme.palette.primaryBackground,
    border: 'solid thin darkgrey',
  },
  grayBackground: {
    background: theme.palette.baseColors.cloudGray.c100,
  },
  loader: {
    ...theme.palette.loading,
  },
  root: {
    ...theme.sizes.fullPageNoAppBar,
    background: theme.palette.baseColors.cloudGray.c100,
  },
  scroll: {
    maxHeight: '100%',
    overflow: 'auto',
    overflowX: 'hidden',
  },
}));

const EditSmartReportPagination = () => {
  const { pages, selectedPage, nextPage, previousPage } = useEditSmartReport();

  const currentPage = selectedPage?.number || 1;
  const numPages = (pages?.length || 1) + STATIC_END_PAGES.length;

  return (
    <Grid container justifyContent='center'>
      <KeyboardArrowLeft onClick={previousPage} />
      <Typography>
        {currentPage}/{numPages}
      </Typography>
      <KeyboardArrowRight onClick={nextPage} />
    </Grid>
  );
};

const EditSmartReportComponent = () => {
  const classes = useStyles();
  const { id } = useParams();
  const { report, loading, error } = useGetReport(id);
  const { setReport, setSelectedLayout } = useEditSmartReport();

  useEffect(() => {
    if (report) {
      setReport(report);
      const layout = SmartReportLayouts.find(
        layout => layout?.id === report.smartReportVersions?.[0]?.pages?.[0]?.layout,
      );
      setSelectedLayout(layout || null);
    }
  }, [report, setReport, setSelectedLayout]);

  if (loading) {
    return (
      <Grid
        container
        className={clsx(classes.loader, classes.root)}
        justifyContent='center'
        alignItems='center'
      >
        <CircularProgress color='inherit' size={20} />
      </Grid>
    );
  }

  if (error) return null;

  if (!report || !id) {
    return <div>Report not found</div>;
  }

  return (
    <Grid container className={classes.root}>
      <Grid
        item
        size={{ md: 2, xxl: 1.5 }}
        className={classes.grayBackground}
        sx={{ height: '100%', padding: '1rem 0 1.5rem 1.5rem' }}
      >
        <Pages />
      </Grid>

      <Grid
        container
        item
        size={{ md: 7, xxl: 8.5 }}
        className={classes.center}
        direction='column'
        padding={3}
        spacing={1}
      >
        <Box margin='0 auto' sx={{ width: 'clamp(30rem, 100%, 140rem)' }}>
          <EditSmartReportHeader />
        </Box>
        <Grid
          container
          flex='1 1 auto'
          margin='1.5rem auto'
          alignItems='center'
          justifyContent='center'
          sx={{
            width: 'clamp(30rem, 100%, 140rem)',
          }}
        >
          <EditPage />
        </Grid>

        <EditSmartReportPagination />
      </Grid>

      <Grid item size={{ md: 3, xxl: 2 }} className={clsx(classes.grayBackground, classes.scroll)}>
        <SideBar />
      </Grid>
    </Grid>
  );
};

export const EditSmartReport = () => {
  return (
    <DndProvider backend={HTML5Backend}>
      <EditSmartReportProvider>
        <EditSmartReportComponent />
      </EditSmartReportProvider>
    </DndProvider>
  );
};
